import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import opportunitiesService from '../../../services/opportunitiesService';
import { Opportunity } from '../../../types';
import { TABLE_FONT_FAMILY } from '../../../theme';
import { daysUntil, getOpportunityStatusLabel } from '../../../utils/commercialPlanning';
import { formatOpportunityDate, nextActionHealth } from '../../../utils/opportunityTable';
import { CAP, hasCapability } from '../../../utils/permissions';
import LastNotePreview from '../../LastNotePreview';

const HEALTH_COLORS: Record<'green' | 'yellow' | 'red' | 'grey', string> = {
    green: '#2e7d32',
    yellow: '#ed6c02',
    red: '#d32f2f',
    grey: '#9e9e9e',
};

function sortByNextActionUrgency(a: Opportunity, b: Opportunity): number {
    const deltaA = daysUntil(a.next_action_date);
    const deltaB = daysUntil(b.next_action_date);

    if (deltaA === null && deltaB === null) return a.title.localeCompare(b.title);
    if (deltaA === null) return 1;
    if (deltaB === null) return -1;
    if (deltaA !== deltaB) return deltaA - deltaB;
    return a.title.localeCompare(b.title);
}

const MyOpportunitiesWidget: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const hasSalesAccess = hasCapability(user, CAP.SALES_TOOLS);

    useEffect(() => {
        const fetchOpportunities = async () => {
            if (!user || !hasSalesAccess) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const all = await opportunitiesService.listOpportunities();
                const owned = all
                    .filter((opp) => opp.commercial_owner_id === user.id)
                    .sort(sortByNextActionUrgency);
                setOpportunities(owned);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch opportunities', err);
                setError('Failed to load opportunities');
            } finally {
                setLoading(false);
            }
        };

        fetchOpportunities();
    }, [user, hasSalesAccess]);

    const gridBorder = theme.palette.mode === 'dark' ? '#2c3e50' : '#e0e0e0';

    const rows = useMemo(() => opportunities, [opportunities]);

    if (!hasSalesAccess) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">
                    Sales access required to view opportunities
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error" variant="body2">{error}</Typography>
            </Box>
        );
    }

    if (rows.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">
                    No opportunities assigned to you
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Table
                size="small"
                sx={{
                    fontFamily: TABLE_FONT_FAMILY,
                    borderCollapse: 'collapse',
                    '& .MuiTableCell-root': {
                        fontFamily: TABLE_FONT_FAMILY,
                        fontSize: '0.8125rem',
                        border: `1px solid ${gridBorder}`,
                        py: 0.75,
                        px: 1,
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                        fontWeight: 700,
                        bgcolor: theme.palette.mode === 'dark' ? '#132436' : '#fafafa',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Next Action</TableCell>
                        <TableCell>Last Note</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((opp) => {
                        const formattedDate = formatOpportunityDate(opp.next_action_date);
                        const delta = formattedDate ? daysUntil(formattedDate) : null;
                        const health = nextActionHealth(opp.next_action_date);
                        const dotColor = health !== 'none' ? HEALTH_COLORS[health] : undefined;

                        return (
                            <TableRow
                                key={opp.id}
                                hover
                                onClick={() => navigate('/sales/leads')}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>{opp.title}</TableCell>
                                <TableCell>{getOpportunityStatusLabel(opp.status)}</TableCell>
                                <TableCell>
                                    {formattedDate ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            {dotColor && (
                                                <Tooltip
                                                    title={
                                                        health === 'red'
                                                            ? 'Overdue'
                                                            : health === 'yellow'
                                                              ? 'Due today or tomorrow'
                                                              : 'On track (2+ days out)'
                                                    }
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 9,
                                                            height: 9,
                                                            borderRadius: '50%',
                                                            bgcolor: dotColor,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                sx={{ fontFamily: TABLE_FONT_FAMILY, fontSize: 'inherit' }}
                                            >
                                                {formattedDate}
                                                {delta !== null ? ` (${delta}d)` : ''}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        '—'
                                    )}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        maxWidth: 200,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <LastNotePreview notes={opp.notes} />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
};

export default MyOpportunitiesWidget;
